/**
 * Google Ad Manager inventory model for the HKEJ revamp.
 *
 * Mirrors the taxonomy in docs/ads/02-gam-architecture-and-mapping.md and the
 * FORMATS table in scripts/ads/gen-mapping.mjs. Unit paths are network-relative:
 *
 *   /<NETWORK_CODE>/<Brand>_Web/<Brand>_Web_<Unit>       display and in-stream (Preroll/Midroll/Postroll)
 * Two levels only: the client's network rejects deeper hierarchies ("The depth of the new ad
 * unit creates a hierarchy that is greater than is allowed"). Codes carry property and platform
 * ([Property]_[Platform]_[Placement]), so the parent is just the <Brand>_<Platform> group.
 *
 * NEXT_PUBLIC_GAM_NETWORK_CODE selects the network (our test network during
 * Track A, the client's 79812692 at cutover). When it is unset the components
 * render their fallback creatives and emit the data-ad-* contract only, so the
 * signed-off pages look exactly as before and the QA harness can still run.
 */

export const GAM_NETWORK_CODE = process.env.NEXT_PUBLIC_GAM_NETWORK_CODE ?? "";
export const ADS_ENABLED = GAM_NETWORK_CODE.length > 0;
/**
 * Pilot mode: only slots that carry an explicit `legacyPath` (an existing ad unit on
 * the client's network, e.g. /79812692/HKEJ_HomePage_LargeRectangle1) make requests;
 * every other slot stays on its fallback creative. Lets a handful of positions run
 * against the client's live GAM before the new taxonomy exists there.
 */
export const GAM_PILOT = process.env.NEXT_PUBLIC_GAM_PILOT === "1";
/**
 * Optional test parent that REPLACES the <Brand>_Web parent, e.g. "zTEST_Revamp" →
 * /79812692/zTEST_Revamp/zTEST_Revamp_HKEJ_Web_LargeRectangle1 (two levels, the network's maximum).
 */
export const GAM_PATH_PREFIX = (process.env.NEXT_PUBLIC_GAM_PATH_PREFIX ?? "").replace(/^\/+|\/+$/g, "");
/**
 * Optional prefix on every unit CODE (e.g. "zTEST_" → zTEST_HKEJ_LargeRectangle1), the GAM
 * test-object naming convention: test units sort to the bottom of every list and can never
 * collide with the real codes, even after the test tree is archived.
 */
export const GAM_CODE_PREFIX = process.env.NEXT_PUBLIC_GAM_CODE_PREFIX ?? "";
/**
 * Optional prefix on every KEY-VALUE name (client requirement for test objects, e.g.
 * "zTEST_Revamp_"). GAM key names are capped at 20 characters, so the two long keys use
 * short forms under a prefix: usertype → user, stockcode → stock.
 */
export const GAM_KEY_PREFIX = process.env.NEXT_PUBLIC_GAM_KEY_PREFIX ?? "";
/** Light box at most once per browser session (legacy showed it on every page view: default off;
 *  frequency caps belong on the line item). NEXT_PUBLIC_AD_POPUP_SESSION_CAP=1 turns it on. */
export const POPUP_SESSION_CAP = process.env.NEXT_PUBLIC_AD_POPUP_SESSION_CAP === "1";
const SHORT_KEY: Record<string, string> = { usertype: "user", stockcode: "stock" };
/** Key name as sent on the wire: "section" → "zTEST_Revamp_section", "usertype" → "zTEST_Revamp_user". */
export function kvKey(name: string): string {
  if (!GAM_KEY_PREFIX) return name;
  const k = `${GAM_KEY_PREFIX}${name}`;
  return k.length <= 20 ? k : `${GAM_KEY_PREFIX}${SHORT_KEY[name] ?? name.slice(0, 20 - GAM_KEY_PREFIX.length)}`;
}
/**
 * Optional allow-list of page paths (locale-less, comma-separated, `*` wildcard) that
 * may request ads; every other page stays on its fallback creatives. Empty = all pages.
 * e.g. "/,/video/detail,/columns/authors/*,/dailynews/headline"
 */
export const GAM_LIVE_PAGES = (process.env.NEXT_PUBLIC_GAM_LIVE_PAGES ?? "").split(",").map((s) => s.trim()).filter(Boolean);
export function isLivePage(path: string): boolean {
  if (!GAM_LIVE_PAGES.length) return true;
  return GAM_LIVE_PAGES.some((pat) => {
    if (pat.endsWith("/*")) { const base = pat.slice(0, -2); return path === base || path.startsWith(base + "/"); }
    return path === pat;
  });
}
/** Optional `env` key-value — only sent when set (staging builds on the client network). */
export const GAM_ENV = process.env.NEXT_PUBLIC_AD_ENV ?? "";

export type Brand =
  | "HKEJ"
  | "Stock360"
  | "LJ"
  | "EJTech"
  | "EJHealth"
  | "EJInsight"
  | "EJEdu"
  | "EJMonthly";

/** URL prefix (after the locale) → brand. First match wins; no match = HKEJ main. */
export const BRAND_PREFIXES: ReadonlyArray<readonly [string, Brand]> = [
  ["/stock360-concepts", "Stock360"],
  ["/stock360", "Stock360"],
  ["/lj", "LJ"],
  ["/ej-tech", "EJTech"],
  ["/ej-health", "EJHealth"],
  ["/ejinsight", "EJInsight"],
  ["/ej-education", "EJEdu"],
  ["/ej-monthly", "EJMonthly"],
];

/** Route prefixes that never carry ads. */
export const NO_AD_PREFIXES = [
  "/account",
  "/auth",
  "/checkout",
  "/legal",
  "/ratecard",
  "/subscription",
  "/select-magazine",
  "/e-paper",
  "/lj/epaper",
  "/ej-monthly/e-paper",
  // Stock360 concept prototypes A/B draw their own placeholder chrome outside the ad layer.
  "/stock360-concepts",
];

export type Device = "desktop" | "tablet" | "mobile";
/** Viewport bands — MUST equal the `lg` (1280) and `md` (768) overrides in app/styles/globals.css. */
export const DEVICE_MIN_WIDTH: Record<Device, number> = { desktop: 1280, tablet: 768, mobile: 0 };
export const DEVICE_ORDER: Device[] = ["desktop", "tablet", "mobile"];

/** "300x250" | "fluid" */
export type SizeToken = string;
export type SizeSet = Record<Device, SizeToken[]>;

export type DisplayFormat =
  | "TopBanner"
  | "HalfPage"
  | "LargeRectangle"
  | "RightBanner"
  | "BigBanner"
  | "InBanner"
  | "NativeAd"
  | "SponsorBanner"
  | "SearchText"
  | "Skinner"
  | "PopUp"
  | "Crazy"
  | "BottomBanner"
  | "InRead"
  | "Interscroller";

export type VideoPosition = "Preroll" | "Midroll" | "Postroll" | "Outstream";

export interface FormatDef {
  /** Chinese + rate-card name, used for the fallback label. */
  label: string;
  /** Sizes requested per viewport band. An empty list = the unit is NOT requested on that band. */
  sizes: SizeSet;
  /** How many numbered units a brand has by default (1 = unnumbered). */
  count: number;
  /** Custom out-of-page unit (defineOutOfPageSlot). */
  outOfPage?: boolean;
  /** Regular slot whose creative the PAGE places (skin wallpaper, light box): no reserved height,
   *  no box-width size filter, no oversize scaling — the host component sizes the creative. */
  overlay?: boolean;
  /** Fluid native unit rendered by a GAM native style. */
  native?: boolean;
  /** Native only: height of the blank box shown while the request is pending (px per band). */
  placeholder?: Record<Device, number>;
  /** The box takes the served creative's height when it is shorter than the reserved one
   *  (HalfPage: a 300x250 in the 600 rail slot would otherwise sit in 350px of white). */
  shrinkToCreative?: boolean;
  /** Growing box (client decision 2026-09-16 for the high-impact formats): the reserved height is
   *  a MINIMUM and the box takes a taller creative or a SafeFrame push expansion, up to `grow` px
   *  (`true` = no cap). Billboard 970x250 / pushdown 970x415 on the page-top band, vendor
   *  outstream tags that resize themselves on InRead. Everything else keeps the fixed height. */
  grow?: number | true;
  /** Explicit reserved height per band (px), instead of the first fixed size's height. */
  reserve?: Partial<Record<Device, number>>;
  /** Scroll-revealed window: a creative taller than the box is shown through it and moved as the
   *  reader scrolls (interscroller / parallax); shorter creatives render as a plain box. */
  parallax?: boolean;
}

export const FORMATS: Record<DisplayFormat, FormatDef> = {
  TopBanner: {
    label: "超級大橫幅",
    // First token per band = the "expected" creative whose height the slot reserves (no CLS);
    // the rest are accepted alternatives. `fluid` lets a responsive HTML creative (image at
    // 100% width, auto height) serve, e.g. a roadblock banner that fills the column.
    // 728x90 stays first on a tablet, so the band reserves 90. 320x100 and 320x50 follow
    // because a page can have three of these slots and each creative serves once: the later
    // slots take a copy that is still free. Fluid is last.
    // 320x100 / 320x50 stay on desktop too: a page can have three top slots and each
    // creative serves once. Slot 1 still takes the billboard or pushdown; slots 2 and 3
    // take a phone-size copy that is still free.
    sizes: { desktop: ["970x160", "970x90", "970x250", "728x90", "970x66", "320x100", "320x50", "fluid"], tablet: ["728x90", "320x100", "320x50", "fluid"], mobile: ["320x100", "320x50", "fluid"] },
    count: 2,
  },
  HalfPage: {
    label: "半版廣告",
    // 160x600 (IAB skyscraper) sits centred in the 300 rail; the reserved 600 stays
    sizes: { desktop: ["300x600", "300x250", "160x600"], tablet: [], mobile: [] },
    count: 1,
    shrinkToCreative: true,
  },
  LargeRectangle: {
    label: "大廣告",
    sizes: { desktop: ["300x250"], tablet: ["300x250"], mobile: ["300x250"] },
    count: 3,
  },
  RightBanner: {
    label: "右方橫幅",
    // 300x90 is the legacy 專題 rail creative size (HKEJ_HomePage_Features_RightBanner1-6)
    sizes: { desktop: ["300x100", "300x90"], tablet: ["300x100", "300x90"], mobile: ["300x100", "300x90"] },
    count: 2,
  },
  BigBanner: {
    label: "內文大橫幅",
    // First token = the creative the band is designed for (its height is reserved); 620x250 is the
    // legacy HKEJ in-content size, kept so old creatives still serve — scaled into the band. The
    // brands whose own creative is the tall one put it first (BRAND_SIZES). Phones take 320x100
    // only: the phone 300x250 in a story is the LargeRectangle placed by MobileArticleLrec
    // (300x250 here could only overflow the 100px band — dropped 2026-09-21).
    sizes: { desktop: ["970x160", "728x90", "620x250"], tablet: ["728x90", "620x250"], mobile: ["320x100"] },
    count: 2,
    // The box follows the creative. A 620x250 in the 160px band used to leave a tall
    // grey container with the image scaled and shifted inside it.
    shrinkToCreative: true,
    grow: 250,
  },
  InBanner: {
    label: "特約文章",
    sizes: { desktop: ["fluid", "620x107"], tablet: ["fluid", "620x107"], mobile: ["fluid", "320x100"] },
    count: 3,
    native: true,
    // blank box while GAM answers (the list row's height); the editorial row takes over if nothing serves
    placeholder: { desktop: 175, tablet: 175, mobile: 140 },
  },
  NativeAd: {
    label: "特約文章",
    sizes: { desktop: ["fluid"], tablet: ["fluid"], mobile: ["fluid"] },
    count: 2,
    native: true,
    placeholder: { desktop: 340, tablet: 340, mobile: 400 },
  },
  SponsorBanner: {
    label: "贊助",
    // phones: the 300x50 logo (legacy EJ Health SponsorLogo) sets the box; 320x40 is centred in it
    sizes: { desktop: ["620x60", "300x50", "300x30", "220x30"], tablet: ["300x50"], mobile: ["300x50", "320x40"] },
    count: 2,
  },
  // Search 精選 chips. Their own units, so they are SearchText1 and SearchText2
  // even when the masthead already holds SponsorBanner1 and SponsorBanner2.
  // Every creative is trafficked at 5×1. That size is a placeholder: sales types
  // the words, and the slot grows to the rendered text (ad-slot listens for the
  // creative's width). A height under 8px does not set the box, so `reserve` does.
  SearchText: {
    label: "精選",
    sizes: {
      desktop: ["5x1"],
      tablet: ["5x1"],
      mobile: ["5x1"],
    },
    count: 2,
    reserve: { desktop: 20, tablet: 20, mobile: 20 },
    grow: true,
  },
  // Skin: a regular slot (an out-of-page slot only ever receives "Out-of-page" creatives, so the
  // legacy image skins could never serve). A plain 1920x1080 (revamp spec) or 1300x1024 (legacy)
  // image is placed behind the boxed column by the page; a 1x1 custom creative draws its own
  // gutters as before (standard 1x1, SafeFrame off).
  Skinner: {
    label: "皮膚廣告",
    // 1600x1000 (the LJ legacy size) is on every unit too: it already exists on the network, so it
    // is offered in the unit's size picker, and the page scales it to the viewport width.
    sizes: { desktop: ["1x1", "1300x1024", "1600x1000", "1920x1080"], tablet: [], mobile: [] },
    count: 1,
    overlay: true,
  },
  // Light box / interstitial: the page draws the shell (legacy: 640x480 in a 680x520 box on
  // desktop, 640x920 scaled to 90 % of the screen on phones); a 1x1 custom creative still
  // overlays by itself.
  PopUp: {
    label: "燈箱廣告",
    // 300x540 = the IAB 9:16 full-page unit on phones (the shell scales it like the 640x920)
    sizes: { desktop: ["1x1", "640x480"], tablet: ["1x1", "640x480", "768x1004", "1024x748"], mobile: ["1x1", "640x920", "320x480", "300x540"] },
    count: 1,
    overlay: true,
  },
  Crazy: {
    label: "全版瘋狂廣告",
    sizes: { desktop: ["1x1", "970x350", "970x600"], tablet: [], mobile: [] },
    count: 1,
    outOfPage: true,
  },
  BottomBanner: {
    label: "廣告",
    // The sticky footer is out of the page flow, so it reserves the tallest size and shrinks to the
    // served creative (a 320x100 in a 50px box used to overlap the page above it).
    sizes: { desktop: [], tablet: ["320x100", "728x90"], mobile: ["320x100", "320x50"] },
    count: 1,
    shrinkToCreative: true,
  },
  // 1x1 / 6x6 are trigger sizes for vendor outstream tags (Teads-style, the legacy InRead): the
  // creative resizes its own iframe, so the box grows with it; reserve nothing on desktop / tablet
  // (a 6px box was reserved before 2026-09-16 because 6x6 was read as a creative height).
  InRead: {
    label: "廣告",
    // 300x250 is the house creative. It stays after the 1x1 / 6x6 vendor triggers so a
    // desktop or tablet slot can still show it; `grow` lets the box take that height.
    sizes: { desktop: ["1x1", "6x6", "fluid", "300x250"], tablet: ["1x1", "6x6", "fluid", "300x250"], mobile: ["1x1", "6x6", "300x250"] },
    count: 1,
    grow: true,
  },
  // Phone interscroller / parallax (client checklist 2026-09-16; legacy: Celtra tags on the phone
  // LREC). A 320x480 IMAGE is revealed through the 250px window as the reader scrolls (the page
  // does the reveal); 300x250 / 1x1 / fluid are for vendor and Google parallax tags that do
  // their own. Phone only; the box is always 250 (fixed-height rule respected).
  Interscroller: {
    label: "廣告",
    sizes: { desktop: [], tablet: [], mobile: ["300x250", "320x480", "1x1", "fluid"] },
    count: 1,
    reserve: { mobile: 250 },
    parallax: true,
  },
};

/** Per-brand overrides of the numbered-unit count (matches gen-mapping.mjs BRAND_FORMAT_COUNT). */
export const BRAND_FORMAT_COUNT: Partial<Record<Brand, Partial<Record<DisplayFormat, number>>>> = {
  // HKEJ LargeRectangle stays 4: the phone home page carries five LREC elements (three rail,
  // two in-flow MobileLrecAd bands) and the fifth wraps onto LargeRectangle1 (client decision
  // 2026-09-16: reuse the existing units rather than add a fifth).
  HKEJ: { TopBanner: 3, LargeRectangle: 4, InBanner: 6, NativeAd: 3, RightBanner: 6, SponsorBanner: 5, SearchText: 2 },
  Stock360: { NativeAd: 5, InBanner: 3, LargeRectangle: 3 },
  EJHealth: { SponsorBanner: 7, InBanner: 5, LargeRectangle: 3 },
  EJMonthly: { LargeRectangle: 4 },
  // page-top LongBanner1 + the summary-grid mid banner + the in-page super banner
  // LJ: two sponsor logos in the masthead on every page (legacy minisite logos 100×40 + 60×40)
  // + the article's byline / after-story strips → 4.
  LJ: { TopBanner: 3, SponsorBanner: 4 },
};

/** Brand-specific GAM unit names kept for Ad Ops continuity (code keeps the canonical name). */
export const BRAND_FORMAT_ALIAS: Partial<Record<Brand, Partial<Record<DisplayFormat, string>>>> = {
  LJ: { TopBanner: "LongBanner" },
};

/** Brand-specific extra sizes. */
export const BRAND_EXTRA_SIZES: Partial<Record<Brand, Partial<Record<DisplayFormat, Partial<SizeSet>>>>> = {
  // LJ's in-page super banner books a 300x250 on phones — on that one slot only (components/lj/super-banner.tsx
  // passes the override, so the band reserves 250 there and the page-top LongBanner1 stays 100).
  LJ: {
    // legacy LJ minisite logo strips in the header (100×40 and 60×40, desktop + tablet)
    SponsorBanner: { desktop: ["100x40", "60x40"], tablet: ["100x40", "60x40"] },
  },
  EJHealth: {
    // sponsor logos beside the section headers (legacy ejhealth SponsorLogo sizes)
    SponsorBanner: { desktop: ["300x70", "260x90", "160x50"], tablet: ["300x70"], mobile: ["300x70"] },
  },
  // EJ Monthly RightBanner 300x250 (legacy HKEJ_Monthly_RightBanner, 2 impressions) dropped 2026-09-21:
  // it overflowed the 100px band; the rail's LargeRectangle units take 300x250.
};

/**
 * A brand whose own creative in a position is not the format's first size REPLACES the band's list
 * (first token = the creative the band reserves height for): the in-content banner is 600x250 /
 * 620x250 on EJ Monthly, 800x250 on EJ Health and 656x90 on Stock360, so those bands are 250 / 250 /
 * 90 tall instead of the 160 that a 970x160 gets elsewhere, and the brand's creative is never scaled.
 */
export const BRAND_SIZES: Partial<Record<Brand, Partial<Record<DisplayFormat, Partial<SizeSet>>>>> = {
  // HKEJ InBanner is the native list row. Image sizes (620x107 / 320x100) were winning
  // on phone and tablet, so the row showed a banner instead of the native ad. Fluid
  // only leaves the native style eligible; an unsold row falls back to the article.
  HKEJ: { InBanner: { desktop: ["fluid"], tablet: ["fluid"], mobile: ["fluid"] } },
  // InBanner is the list row on 大巿動向 and the other timeline tabs. The image
  // sizes were winning, so the row showed a 620×107 / 320×100 banner.
  Stock360: {
    BigBanner: { desktop: ["656x90", "656x80", "656x50", "728x90"], tablet: ["656x80", "728x90"], mobile: ["320x100", "320x50"] },
    InBanner: { desktop: ["fluid"], tablet: ["fluid"], mobile: ["fluid"] },
  },
  EJHealth: {
    BigBanner: { desktop: ["800x250", "620x250", "970x160", "728x90"], tablet: ["620x250", "728x90"] },
    // InBanner is the native card on the listings (every 3rd) and the home
    // topic grids. 820x160 / 620x107 / 320x380 were winning on tablet and
    // phone, so those widths showed a banner or nothing instead of the native.
    InBanner: { desktop: ["fluid"], tablet: ["fluid"], mobile: ["fluid"] },
  },
  EJMonthly: {
    BigBanner: { desktop: ["600x250", "620x250", "970x160", "728x90"], tablet: ["620x250", "600x250", "728x90"] },
    // InBanner is the list row on 信月推介 and the issue pages. 620x107 / 320x100
    // were winning on tablet and phone, so the row showed a banner instead of
    // the native article. Fluid only leaves the list-row style eligible.
    InBanner: { desktop: ["fluid"], tablet: ["fluid"], mobile: ["fluid"] },
  },
  // EJ Tech InBanner is the list row on the home topic blocks and the category
  // and tag listings. 320x100 was winning on a phone, so the row showed a
  // banner instead of the native article. Fluid only leaves the list-row style
  // eligible; an unsold row falls back to the article.
  EJTech: { InBanner: { desktop: ["fluid"], tablet: ["fluid"], mobile: ["fluid"] } },
};

export function countFor(brand: Brand, format: DisplayFormat): number {
  return BRAND_FORMAT_COUNT[brand]?.[format] ?? FORMATS[format].count;
}

/** "LargeRectangle2" → { format: "LargeRectangle", index: 2 }; "Skinner" → { format, index: undefined }; "LargeRectangle" → auto. */
export function parseUnit(unit: string): { format: DisplayFormat; index?: number } {
  const m = unit.match(/^([A-Za-z]+?)(\d+)?$/);
  if (!m || !(m[1] in FORMATS)) throw new Error(`Unknown ad unit "${unit}"`);
  return { format: m[1] as DisplayFormat, index: m[2] ? parseInt(m[2], 10) : undefined };
}

/** GAM leaf code for a resolved unit — [Property]_[Platform]_[Placement], e.g. HKEJ_Web_LargeRectangle2, LJ_Web_LongBanner1, HKEJ_Web_Skinner. */
export function unitCode(brand: Brand, format: DisplayFormat, index?: number): string {
  const alias = BRAND_FORMAT_ALIAS[brand]?.[format] ?? format;
  const numbered = countFor(brand, format) > 1;
  return `${GAM_CODE_PREFIX}${brand}_Web_${alias}${numbered ? String(index ?? 1) : ""}`;
}

/** The single parent level: <Brand>_Web in production, the test parent (e.g. zTEST_Revamp) in test mode. */
const parent = (brand: Brand) => GAM_PATH_PREFIX || `${brand}_Web`;

export function unitPath(brand: Brand, code: string): string {
  return `/${GAM_NETWORK_CODE}/${parent(brand)}/${code}`;
}

export function videoUnitPath(brand: Brand, position: VideoPosition): string {
  return `/${GAM_NETWORK_CODE}/${parent(brand)}/${GAM_CODE_PREFIX}${brand}_Web_${position}`;
}

/** Sizes for one unit on one brand, per band (format defaults ∪ brand extras). */
export function sizesFor(brand: Brand, format: DisplayFormat, override?: Partial<SizeSet>): SizeSet {
  const base = FORMATS[format].sizes;
  const extra = BRAND_EXTRA_SIZES[brand]?.[format];
  const own = BRAND_SIZES[brand]?.[format];
  const out = {} as SizeSet;
  for (const d of DEVICE_ORDER) {
    if (override && override[d] !== undefined) out[d] = [...(override[d] as SizeToken[])];
    else if (own && own[d] !== undefined) out[d] = [...(own[d] as SizeToken[])];
    else out[d] = [...(base[d] ?? []), ...((extra?.[d] as SizeToken[] | undefined) ?? [])];
  }
  return out;
}

/**
 * DOM-order numbering with wrap-around overflow. Explicit indices are reserved first; each
 * auto slot takes the lowest free number. Once every number of a format is taken, further
 * slots wrap around (5 → 1, 6 → 2 …) instead of piling up on the last unit — an overflow
 * slot shares a unit's line items and reporting, which is what we want for "more slots than
 * the rate card knows", and never a duplicate of one designed position. Pure, so it can be
 * tested outside React.
 */
export function nextIndex(taken: Set<number>, from: number, max: number, overflow: number): { index: number; from: number; overflow: number; wrapped: boolean } {
  let n = Math.max(1, from);
  while (n <= max && taken.has(n)) n++;
  if (n <= max) {
    taken.add(n);
    return { index: n, from: n + 1, overflow, wrapped: false };
  }
  return { index: (overflow % max) + 1, from: n, overflow: overflow + 1, wrapped: true };
}

export type GptSize = [number, number] | "fluid";
export function toGptSize(token: SizeToken): GptSize {
  if (token === "fluid") return "fluid";
  const [w, h] = token.split("x").map((n) => parseInt(n, 10));
  return [w, h];
}

/**
 * Height reserved for a band: the FIRST fixed size in the list is the expected creative
 * (970x160 on desktop, 728x90 on tablet, 320x100 on a phone for the top banner). The box
 * keeps that height whether the ad is pending, served or empty, so the page never jumps
 * (client rule 2026-09-09); a shorter alternative size is centred inside it.
 */
export function primaryHeight(tokens: SizeToken[]): number {
  for (const t of tokens) {
    if (t === "fluid") continue;
    const [, hh] = t.split("x").map((n) => parseInt(n, 10));
    // 1x1 / 2x2 / 6x6 … are trigger sizes (a script creative), never a box height
    if (hh > 8) return hh;
  }
  return 0;
}

/** The height a slot reserves on a band: the format's explicit `reserve`, else the first fixed size. */
export function reservedFor(def: FormatDef, tokens: SizeToken[], device: Device): number {
  const r = def.reserve?.[device];
  return r !== undefined ? r : primaryHeight(tokens);
}

/** Tallest fixed creative on a band (preview boxes). */
export function reservedHeight(tokens: SizeToken[]): number {
  let h = 0;
  for (const t of tokens) {
    if (t === "fluid") continue;
    const [, hh] = t.split("x").map((n) => parseInt(n, 10));
    if (hh > 1 && hh > h) h = hh;
  }
  return h;
}

export function deviceForWidth(width: number): Device {
  if (width >= DEVICE_MIN_WIDTH.desktop) return "desktop";
  if (width >= DEVICE_MIN_WIDTH.tablet) return "tablet";
  return "mobile";
}
